import { For, Show } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { ArrowUpRight } from "lucide-solid";
import { DICT, localeFromPath, prefixFromPath, segmentFromPath, hardcoded } from "@/context/locale";
import { LANGUAGES } from "@/locales/registry";
import { SUPPORT_URL } from "@/lib/support";

const SOCIALS = [
  { label: "GitHub", href: "https://github.com/lyszt" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/lyszt/" },
  { label: "YouTube", href: "https://www.youtube.com/@lyszt" },
  { label: "Instagram", href: "https://www.instagram.com/fauxkaldwin" },
];

function SiteLink(props) {
  return (
    <A
      href={props.href}
      class="text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
    >
      {props.children}
    </A>
  );
}

export default function Footer() {
  const location = useLocation();
  const locale = () => localeFromPath(location.pathname);
  const prefix = () => prefixFromPath(location.pathname);
  const t = (key) => {
    let value = DICT[locale()];
    for (const part of key.split(".")) {
      if (value == null) return key;
      value = value[part];
    }
    return value ?? key;
  };
  const pagePath = () => location.pathname.replace(`/${segmentFromPath(location.pathname)}`, "");
  const year = new Date().getFullYear();
  const links = () => [
    { label: t("breadcrumb.home"), href: prefix() + "/" },
    { label: t("breadcrumb.portfolio"), href: prefix() + "/portfolio" },
    { label: t("breadcrumb.articles"), href: prefix() + "/academia/articles" },
    { label: t("breadcrumb.curriculum"), href: prefix() + "/academia/curriculum" },
    { label: hardcoded("Lux"), href: prefix() + "/toolkit/litessera-lux?view=full" },
  ];

  return (
    <footer class="no-print w-full border-t border-border bg-surface/45 text-foreground">
      <div class="mx-auto max-w-7xl px-5 py-10 sm:px-8 md:py-12 lg:px-10 lg:pb-28">
        <div class="grid gap-10 md:grid-cols-[minmax(12rem,1fr)_minmax(20rem,1.35fr)] md:gap-12 lg:grid-cols-[minmax(14rem,0.8fr)_minmax(24rem,1.4fr)_auto] lg:items-start">
          <div>
            <A
              href={prefix() + "/"}
              class="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.18em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span class="size-1.5 rounded-full bg-purple" />
              LYSZT
            </A>
            <p class="mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>

          <nav aria-label={t("footer.explore")}>
            <p class="mb-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("footer.explore")}
            </p>
            <div class="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
              <For each={links()}>
                {(link) => <SiteLink href={link.href}>{link.label}</SiteLink>}
              </For>
            </div>
          </nav>

          <A
            href={prefix() + "/contact"}
            class="group inline-flex min-h-10 w-fit items-center gap-3 rounded-full border border-border bg-background px-4 text-sm font-medium shadow-sm transition-colors hover:border-foreground/30 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("breadcrumb.contact")}
            <ArrowUpRight aria-hidden="true" size={14} class="text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </A>
        </div>

        <nav aria-label={t("nav.language")} class="mt-10 border-t border-border pt-6">
          <p class="mb-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("nav.language")}
          </p>
          <ul class="flex flex-wrap gap-x-6 gap-y-3">
            <For each={LANGUAGES}>
              {(lang) => (
                <li>
                  <A
                    href={`/${lang.segment}${pagePath()}`}
                    lang={lang.hreflang}
                    hreflang={lang.hreflang}
                    aria-current={lang.code === locale() ? "true" : undefined}
                    class="text-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
                    classList={{
                      "text-foreground": lang.code === locale(),
                      "text-muted-foreground": lang.code !== locale(),
                    }}
                  >
                    {lang.native}
                  </A>
                </li>
              )}
            </For>
          </ul>
        </nav>

        <div class="mt-10 flex flex-col gap-5 border-t border-border pt-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <p>© {year} {hardcoded("LYSZT:")} <a href="https://lygon.litessera.com">{hardcoded("Powered by Lygon")}</a>. {t("footer.rights")}</p>

          <div class="flex flex-wrap items-center gap-x-5 gap-y-3">
            <For each={SOCIALS}>
              {(social) => (
                <a
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
                >
                  {social.label}
                </a>
              )}
            </For>
            <a
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              class="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
            >
              {t("footer.support")}
            </a>
            <span aria-hidden="true" class="hidden h-3 w-px bg-border sm:block" />
            <A href={prefix() + "/terms"} class="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground">
              {t("footer.terms")}
            </A>
            <A href={prefix() + "/privacy"} class="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground">
              {t("footer.privacy")}
            </A>
            <Show when={import.meta.env.DEV}>
              <A href="/dev/lessons" class="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground">
                {hardcoded("Lesson editor")}
              </A>
            </Show>
          </div>
        </div>
      </div>
    </footer>
  );
}
